// One tempo per session (automation plan A3): every local edit goes on the wire, and every client adopts what arrives, so a runner handover keeps the beat.
import tempo from './tempo'

let send = null
let quiet = false
// A server that keeps a tempo sends one (or `null`) with every join snapshot; an older one would take the message for a command.
let supported = false
// The wire's `at` is on the session's clock: identity on a desktop, the runner's clock on a remote (C-tempo).
let toWire = t => t
let fromWire = t => t
// False on a remote: it never seeds an empty session with its own clock (R12).
let seeds = true
// A clock that arrived before `fromWire` could place it (a remote before its first pong): adopted once it can.
let pending = null

const wire = () => ({ ...tempo.state(), at: toWire(Date.now()) })

const adopt = payload => {
	const at = fromWire(payload.at)
	if (at == null) return (pending = payload)
	pending = null
	// The sender's wall clock dated the reading; the transit since moves it on.
	quietly(() => tempo.adopt(payload, performance.now() - (Date.now() - at)))
}

/** Run `fn` without putting its tempo change on the wire: a project load, or a clock arriving from it. */
export const quietly = fn => {
	quiet = true
	try {
		fn()
	} finally {
		quiet = false
	}
}

/**
 * Put this client's edits on the wire through `sendFn(state)`, `at` mapped by `clock` `{ toWire, fromWire, onChange, seeds }`: `fromWire` returns null while it cannot map, and `onChange(fn)` retries a clock held meanwhile. Returns the stop.
 */
export const startTempoSync = (sendFn, clock) => {
	send = sendFn
	let offClock = null
	if (clock) {
		;({ toWire, fromWire } = clock)
		seeds = clock.seeds ?? true
		offClock = clock.onChange?.(() => pending && adopt(pending))
	}
	const off = tempo.subscribe(() => {
		if (quiet || !supported) return
		pending = null
		send?.(wire())
	})
	return () => {
		off()
		offClock?.()
		send = null
		pending = null
		seeds = true
		toWire = fromWire = t => t
	}
}

/** A peer's clock, or `null` from a server that has none yet: a desktop's own then seeds the session. */
export const receiveTempo = payload => {
	supported = true
	if (!payload) return seeds ? send?.(wire()) : undefined
	adopt(payload)
}
